"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Popover = void 0;
var _react = _interopRequireWildcard(require("react"));
require("./Popover.css");
var _cdsCommon = require("@cbhq/cds-common");
var _useSpectrumConditional = require("@cbhq/cds-common/hooks/useSpectrumConditional");
var _zIndex = require("@cbhq/cds-common/tokens/zIndex");
var _NewAnimatePresence = require("../../animation/NewAnimatePresence");
var _Box = require("../../layout/Box");
var _system = require("../../system");
var _linaria = require("../../utils/linaria");
var _FocusTrap = require("../FocusTrap");
var _Overlay = require("../Overlay/Overlay");
var _Portal = require("../Portal");
var _PortalProvider = require("../PortalProvider");
var _usePopper2 = require("./usePopper");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); } /* eslint-disable jsx-a11y/no-static-element-interactions */ /* eslint-disable jsx-a11y/click-events-have-key-events */
const subjectStyle = "cds-subjectStyle-s1itmecy";
const defaultContentPosition = {
  gap: 0,
  skid: 0,
  placement: 'bottom',
  offsetGap: undefined
};
const blockStyles = "cds-blockStyles-b1fr0m9x";
const inverseConfig = {
  light: 'dark',
  dark: 'light'
};

/**
 * Popover is the internal recommended base component used for any overlay that is laid out with respect to a subject.
 * It is purposely a flexible component and is reserved for CDS internal usage.
 */
const Popover = /*#__PURE__*/(0, _react.memo)(({
  content,
  children,
  disablePortal,
  showOverlay = false,
  onPressSubject,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onMouseDown,
  invertPopoverSpectrum,
  visible,
  accessibilityLabel = 'popoverMenu',
  testID,
  contentPosition = defaultContentPosition,
  block = false,
  disableTypeFocus = false,
  disabled,
  disableAutoFocus = false,
  controlledElementAccessibilityProps
}) => {
  const _usePopper = (0, _usePopper2.usePopper)(contentPosition),
    setSubject = _usePopper.setSubject,
    setPopper = _usePopper.setPopper,
    popperStyles = _usePopper.popperStyles,
    popperAttributes = _usePopper.popperAttributes;
  const scale = (0, _cdsCommon.useScale)();
  const invertedSpectrum = (0, _useSpectrumConditional.useSpectrumConditional)(inverseConfig);

  // We use this to infer that hover events are triggering the mounting/dismounting of the content
  const hasHoverInteractions = !!onMouseEnter && !!onMouseLeave && !onPressSubject;
  const shouldShowContent = visible && !disabled;
  const handleClose = (0, _react.useCallback)(async () => {
    onClose === null || onClose === void 0 ? void 0 : onClose();
    onBlur === null || onBlur === void 0 ? void 0 : onBlur();
  }, [onBlur, onClose]);

  // swallow click events inside the Popover content so the overlay doesn't consider it a blur event
  const handleCaptureEvents = (0, _react.useCallback)(event => {
    event.stopPropagation();
  }, []);
  const memoizedContent = (0, _react.useMemo)(() => /*#__PURE__*/_react.default.createElement("div", _extends({
    ref: setPopper,
    style: Object.assign({}, popperStyles.popper, {
      zIndex: _zIndex.zIndex.overlays.dropdown
    })
  }, popperAttributes.popper, {
    onClick: handleCaptureEvents
  }), /*#__PURE__*/_react.default.createElement(_FocusTrap.FocusTrap, {
    disableAutoFocus: disableAutoFocus,
    disableTypeFocus: disableTypeFocus,
    onEscPress: handleClose
  }, /*#__PURE__*/_react.default.createElement(_Box.Box, _extends({
    dangerouslySetBackground: "transparent",
    testID: testID
  }, controlledElementAccessibilityProps), content))), [setPopper, popperStyles.popper, popperAttributes.popper, handleCaptureEvents, handleClose, disableTypeFocus, testID, content, disableAutoFocus, controlledElementAccessibilityProps]);
  const renderContent = hasHoverInteractions ? memoizedContent : /*#__PURE__*/_react.default.createElement(_Box.Box, {
    accessibilityLabel: accessibilityLabel,
    "aria-modal": "true",
    onClick: handleClose,
    pin: "all",
    position: "fixed",
    role: "dialog",
    zIndex: _zIndex.zIndex.overlays.portal + _zIndex.zIndex.overlays.modal
  }, memoizedContent);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: block ? blockStyles : undefined,
    onMouseEnter: disabled ? undefined : onMouseEnter,
    onMouseLeave: disabled ? undefined : onMouseLeave
  }, /*#__PURE__*/_react.default.createElement("div", {
    ref: setSubject,
    className: (0, _linaria.cx)(subjectStyle, block ? blockStyles : undefined),
    onBlur: onBlur,
    onClick: disabled ? undefined : onPressSubject,
    onFocus: disabled ? undefined : onFocus,
    onMouseDown: disabled ? undefined : onMouseDown
  }, children), /*#__PURE__*/_react.default.createElement(_NewAnimatePresence.NewAnimatePresence, null, shouldShowContent ? /*#__PURE__*/_react.default.createElement(_Portal.Portal, {
    containerId: _PortalProvider.tooltipContainerId,
    disablePortal: disablePortal
  }, /*#__PURE__*/_react.default.createElement(_system.ThemeProvider, {
    scale: scale,
    spectrum: invertPopoverSpectrum ? invertedSpectrum : null
  }, showOverlay ? /*#__PURE__*/_react.default.createElement(_Box.Box, {
    pin: "all",
    position: "fixed",
    zIndex: _zIndex.zIndex.overlays.portal + _zIndex.zIndex.overlays.modal
  }, /*#__PURE__*/_react.default.createElement(_Overlay.Overlay, {
    animated: true,
    onPress: handleClose
  }), memoizedContent) : renderContent)) : undefined));
});
exports.Popover = Popover;